import { Card, Grid, Menu, MenuItem } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import useGetJobReadinessDashboard from "hooks/jobs/useGetJobReadinessDashboard";
import dayjs from "dayjs";
import DispatchChecklistModal from "modules/jobs/DispatchChecklistModal";
import DispatchReadinessTable from "modules/jobs/DispatchReadinessTable";
import WorkOrderModal from "modules/jobs/WorkOrder/WorkOrderModal";
import { MouseEvent, useCallback, useEffect, useMemo, useState } from "react";
import {
  formatChecklistStatus,
  formatDateLabel,
  formatMoney,
  formatQty,
  groupBySource,
} from "modules/jobs/dispatchReadinessTableUtils";

const escapeCsvValue = (value: string | number | boolean | null | undefined) => {
  const normalized = value == null ? "" : String(value);
  return `"${normalized.replace(/"/g, '""')}"`;
};

const escapeHtml = (value: string | number | boolean | null | undefined) => {
  const normalized = value == null ? "" : String(value);
  return normalized
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
};

function KpiCard({ label, value }: { label: string; value: string | number }) {
  return (
    <Card>
      <MDBox p={1.5}>
        <MDTypography variant="caption" color="text" textTransform="uppercase">
          {label}
        </MDTypography>
        <MDTypography variant="h6" lineHeight={1.2}>
          {value}
        </MDTypography>
      </MDBox>
    </Card>
  );
}

export default function DispatchDaySummary({
  date,
  onClose,
}: {
  date: string;
  onClose: () => void;
}) {
  const [exportMenuAnchorEl, setExportMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedChecklistPhaseId, setSelectedChecklistPhaseId] = useState<string | null>(null);
  const [selectedWorkOrderPhaseId, setSelectedWorkOrderPhaseId] = useState<string | null>(null);
  const { getJobReadinessDashboard, readinessData, loading, error } = useGetJobReadinessDashboard();

  useEffect(() => {
    getJobReadinessDashboard({ startDate: date, endDate: date });
  }, [date, getJobReadinessDashboard]);

  const handleExportCsv = useCallback(() => {
    const headers = [
      "Scheduled Date",
      "Job External ID",
      "Job Name",
      "Job Phase ID",
      "Confirmed With Client",
      "Checklist Outstanding",
      "Checklist Total",
      "Revenue Scheduled",
      "Profit Scheduled",
      "Material Source",
      "Material",
      "Quantity",
      "Unit",
      "Trucks",
      "Has Plant",
    ];

    const rows = readinessData.jobs.flatMap((job) => {
      if (job.materialLines.length === 0) {
        return [
          [
            job.scheduledDate,
            job.jobExternalId || "",
            job.jobName || "",
            job.jobPhaseId,
            job.confirmedWithClient ? "Yes" : "No",
            job.checklistOutstanding,
            job.checklistTotal,
            job.revenueScheduled,
            job.profitScheduled,
            "",
            "",
            "",
            "",
            "",
            "",
          ],
        ];
      }

      return job.materialLines.map((line) => [
        job.scheduledDate,
        job.jobExternalId || "",
        job.jobName || "",
        job.jobPhaseId,
        job.confirmedWithClient ? "Yes" : "No",
        job.checklistOutstanding,
        job.checklistTotal,
        job.revenueScheduled,
        job.profitScheduled,
        line.sourceName,
        line.materialName,
        line.quantity,
        line.unitAbbr,
        line.trucks,
        line.hasPlant ? "Yes" : "No",
      ]);
    });

    const csv = [headers, ...rows].map((row) => row.map(escapeCsvValue).join(",")).join("\n");
    const blob = new Blob(["\uFEFF", csv], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `dispatch-day-readiness-${date}.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    window.URL.revokeObjectURL(url);
  }, [date, readinessData.jobs]);

  const handleExportPdf = useCallback(() => {
    const generatedAt = dayjs().format("MMM D, YYYY h:mm A");

    const jobsRowsHtml =
      readinessData.jobs.length === 0
        ? `<tr><td colspan="6">No scheduled jobs found for the selected day.</td></tr>`
        : readinessData.jobs
            .map((job) => {
              const materialPlan =
                job.materialLines.length === 0
                  ? "No material lines"
                  : Object.entries(groupBySource(job.materialLines))
                      .map(([source, lines]) => {
                        const lineRows = lines
                          .map(
                            (line) =>
                              `${escapeHtml(line.materialName)} | ${escapeHtml(
                                formatQty(line.quantity)
                              )} ${escapeHtml(line.unitAbbr)} | ${escapeHtml(line.trucks)}`
                          )
                          .join("<br />");

                        return `<div><strong>${escapeHtml(source)}</strong><br />${lineRows}</div>`;
                      })
                      .join("<br />");

              return `
                <tr>
                  <td>
                    <strong>${escapeHtml(
                      job.jobExternalId || `Phase ${job.jobPhaseId}`
                    )}</strong><br />
                    ${escapeHtml(job.jobName || "Unnamed job")}
                  </td>
                  <td>${escapeHtml(
                    formatChecklistStatus(job.checklistTotal, job.checklistOutstanding)
                  )}</td>
                  <td>${job.confirmedWithClient ? "Yes" : "No"}</td>
                  <td>${materialPlan}</td>
                  <td class="num">${escapeHtml(formatMoney(job.revenueScheduled))}</td>
                  <td class="num">${escapeHtml(formatMoney(job.profitScheduled))}</td>
                </tr>
              `;
            })
            .join("");

    const totalsRowsHtml =
      readinessData.dailyTotals.length === 0
        ? `<tr><td colspan="5">No daily totals found.</td></tr>`
        : readinessData.dailyTotals
            .flatMap((day) =>
              day.materialRows.map(
                (line) => `
                <tr>
                  <td>${escapeHtml(formatDateLabel(day.scheduledDate))}</td>
                  <td>${escapeHtml(line.sourceName)}</td>
                  <td>${escapeHtml(line.materialName)}</td>
                  <td class="num">${escapeHtml(formatQty(line.quantity))} ${escapeHtml(
                  line.unitAbbr
                )}</td>
                  <td class="num">${escapeHtml(line.trucks)}</td>
                </tr>
              `
              )
            )
            .join("");

    const html = `
      <!doctype html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>Dispatch Summary ${escapeHtml(date)}</title>
          <style>
            :root { --text: #1f2d3d; --muted: #5b6b7c; --line: #d9e1ea; --soft: #f5f8fc; --warn: #8f5a00; --warn-bg: #fff7e0; }
            * { box-sizing: border-box; }
            body { margin: 0; font-family: "Avenir Next", "Segoe UI", sans-serif; color: var(--text); background: #fff; }
            .page { padding: 18px 22px; }
            .head { display: grid; grid-template-columns: 1.3fr 1fr; gap: 18px; border-bottom: 1px solid var(--line); padding-bottom: 14px; }
            h1 { margin: 0; font-size: 24px; }
            .sub { margin: 6px 0 0; color: var(--muted); font-size: 13px; }
            .meta { border: 1px solid var(--line); background: var(--soft); border-radius: 10px; padding: 10px 12px; font-size: 12px; }
            .meta-row { display: flex; justify-content: space-between; margin-bottom: 6px; }
            .meta-row:last-child { margin-bottom: 0; }
            .kpi-row { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 8px; margin-top: 14px; }
            .kpi { border: 1px solid var(--line); border-radius: 10px; background: #fff; padding: 8px; min-width: 0; }
            .kpi .label { font-size: 10px; color: var(--muted); line-height: 1.2; }
            .kpi .value { margin-top: 5px; font-size: 13px; font-weight: 700; line-height: 1.2; word-break: break-word; overflow-wrap: anywhere; }
            .section { margin-top: 14px; }
            .section h2 { margin: 0 0 10px; font-size: 15px; }
            .alert { border: 1px solid #f2d39f; background: var(--warn-bg); border-radius: 10px; padding: 10px; font-size: 12px; color: var(--warn); }
            table { width: 100%; border-collapse: collapse; }
            th, td { border: 1px solid var(--line); padding: 7px 8px; font-size: 12px; text-align: left; vertical-align: top; }
            th { background: var(--soft); }
            .num { text-align: right; font-variant-numeric: tabular-nums; }
            @media print { @page { size: auto; margin: 10mm; } }
          </style>
          <script>
            window.addEventListener("load", function () {
              setTimeout(function () { window.print(); }, 250);
            });
          </script>
        </head>
        <body>
          <main class="page">
            <section class="head">
              <div>
                <h1>Daily Dispatch Summary</h1>
                <p class="sub">Schedule readiness export for operations, dispatch, and plant coordination.</p>
              </div>
              <div class="meta">
                <div class="meta-row"><strong>Date</strong><span>${escapeHtml(
                  formatDateLabel(date)
                )}</span></div>
                <div class="meta-row"><strong>Generated</strong><span>${escapeHtml(
                  generatedAt
                )}</span></div>
                <div class="meta-row"><strong>Jobs</strong><span>${escapeHtml(
                  readinessData.summary.jobsScheduled
                )}</span></div>
                <div class="meta-row"><strong>Confirmed</strong><span>${escapeHtml(
                  readinessData.summary.jobsConfirmedWithClient
                )} / ${escapeHtml(readinessData.summary.jobsScheduled)}</span></div>
              </div>
            </section>
            <section class="kpi-row">
              <article class="kpi"><div class="label">Jobs Scheduled</div><div class="value">${escapeHtml(
                readinessData.summary.jobsScheduled
              )}</div></article>
              <article class="kpi"><div class="label">Confirmed w/ Client</div><div class="value">${escapeHtml(
                readinessData.summary.jobsConfirmedWithClient
              )} / ${escapeHtml(readinessData.summary.jobsScheduled)}</div></article>
              <article class="kpi"><div class="label">Revenue Scheduled</div><div class="value">${escapeHtml(
                formatMoney(readinessData.summary.revenueScheduled)
              )}</div></article>
              <article class="kpi"><div class="label">Profit Scheduled</div><div class="value">${escapeHtml(
                formatMoney(readinessData.summary.profitScheduled)
              )}</div></article>
            </section>
            <section class="section">
              <h2>Dispatch Exceptions</h2>
              <div class="alert">
                ${escapeHtml(
                  readinessData.exceptions.jobsNotConfirmedWithClient
                )} jobs not confirmed with client,
                ${escapeHtml(
                  readinessData.exceptions.jobsMissingPlantAssignment
                )} jobs missing plant assignment,
                ${escapeHtml(
                  readinessData.exceptions.jobsMissingMaterialQuantity
                )} jobs missing material quantity,
                ${escapeHtml(
                  readinessData.exceptions.materialLinesWithoutTrucks
                )} material lines without trucks.
              </div>
            </section>
            <section class="section">
              <h2>Job Readiness</h2>
              <table>
                <thead>
                  <tr>
                    <th>Job</th>
                    <th>Checklist</th>
                    <th>Confirmed</th>
                    <th>Material Plan (Plant / Material / Qty / Unit / Trucks)</th>
                    <th>Revenue</th>
                    <th>Profit</th>
                  </tr>
                </thead>
                <tbody>${jobsRowsHtml}</tbody>
              </table>
            </section>
            <section class="section">
              <h2>Daily Material Totals</h2>
              <table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Plant</th>
                    <th>Material</th>
                    <th>Qty</th>
                    <th>Trucks</th>
                  </tr>
                </thead>
                <tbody>${totalsRowsHtml}</tbody>
              </table>
            </section>
          </main>
        </body>
      </html>
    `;

    const blob = new Blob([html], { type: "text/html;charset=utf-8" });
    const blobUrl = window.URL.createObjectURL(blob);
    const printWindow = window.open(blobUrl, "_blank");
    if (!printWindow) {
      window.URL.revokeObjectURL(blobUrl);
      return;
    }

    setTimeout(() => {
      window.URL.revokeObjectURL(blobUrl);
    }, 60000);
  }, [
    date,
    readinessData.dailyTotals,
    readinessData.exceptions,
    readinessData.jobs,
    readinessData.summary,
  ]);

  const handleOpenExportMenu = useCallback((event: MouseEvent<HTMLElement>) => {
    setExportMenuAnchorEl(event.currentTarget);
  }, []);

  const handleCloseExportMenu = useCallback(() => {
    setExportMenuAnchorEl(null);
  }, []);

  const handleExportCsvClick = useCallback(() => {
    handleCloseExportMenu();
    handleExportCsv();
  }, [handleCloseExportMenu, handleExportCsv]);

  const handleExportPdfClick = useCallback(() => {
    handleCloseExportMenu();
    handleExportPdf();
  }, [handleCloseExportMenu, handleExportPdf]);

  const dayTitle = useMemo(() => formatDateLabel(date), [date]);
  const handleCloseChecklistModal = useCallback(() => {
    setSelectedChecklistPhaseId(null);
    getJobReadinessDashboard({ startDate: date, endDate: date });
  }, [date, getJobReadinessDashboard]);
  const handleCloseWorkOrderModal = useCallback(() => {
    setSelectedWorkOrderPhaseId(null);
    getJobReadinessDashboard({ startDate: date, endDate: date });
  }, [date, getJobReadinessDashboard]);

  return (
    <MDBox p={2} pt={3} pr={9}>
      <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <div>
          <MDTypography variant="h4">Daily Schedule Readiness</MDTypography>
          <MDTypography variant="button" color="text">
            Planning snapshot for checklist completion, materials, and trucking.
          </MDTypography>
          <MDBox mt={1}>
            <MDTypography variant="caption" color="info" fontWeight="medium">
              Selected Day: {dayTitle}
            </MDTypography>
          </MDBox>
        </div>
        <MDBox display="flex" gap={1}>
          <MDButton
            variant="gradient"
            color="info"
            onClick={handleOpenExportMenu}
            disabled={loading || readinessData.jobs.length === 0}
          >
            Export Day Summary
          </MDButton>
          <Menu
            anchorEl={exportMenuAnchorEl}
            open={Boolean(exportMenuAnchorEl)}
            onClose={handleCloseExportMenu}
          >
            <MenuItem onClick={handleExportCsvClick}>Export CSV</MenuItem>
            <MenuItem onClick={handleExportPdfClick}>Export PDF</MenuItem>
          </Menu>
        </MDBox>
      </MDBox>

      {error ? (
        <MDTypography variant="caption" color="error">
          Failed to load dispatch readiness data for this day.
        </MDTypography>
      ) : null}

      <Grid container spacing={1.5}>
        <Grid item xs={12} md={3}>
          <KpiCard
            label="Revenue Scheduled"
            value={loading ? "..." : formatMoney(readinessData.summary.revenueScheduled)}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <KpiCard
            label="Profit Scheduled"
            value={loading ? "..." : formatMoney(readinessData.summary.profitScheduled)}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <KpiCard
            label="Jobs Scheduled"
            value={loading ? "..." : readinessData.summary.jobsScheduled}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <KpiCard
            label="Jobs Confirmed"
            value={
              loading
                ? "..."
                : `${readinessData.summary.jobsConfirmedWithClient} / ${readinessData.summary.jobsScheduled}`
            }
          />
        </Grid>

        <Grid item xs={12}>
          <Card sx={{ background: "#fffaf0", borderLeft: "5px solid #f59f00" }}>
            <MDBox p={2} display="flex" flexWrap="wrap" alignItems="center" gap={1}>
              <MDTypography variant="button" fontWeight="medium">
                Exceptions to resolve before dispatch:
              </MDTypography>
              <MDBox
                component="span"
                sx={{ px: 1, py: 0.25, borderRadius: 999, background: "#ffe3e3", fontSize: 12 }}
              >
                {readinessData.exceptions.jobsNotConfirmedWithClient} jobs not confirmed
              </MDBox>
              <MDBox
                component="span"
                sx={{ px: 1, py: 0.25, borderRadius: 999, background: "#fff3cd", fontSize: 12 }}
              >
                {readinessData.exceptions.jobsMissingPlantAssignment} jobs missing plant
              </MDBox>
              <MDBox
                component="span"
                sx={{ px: 1, py: 0.25, borderRadius: 999, background: "#fff3cd", fontSize: 12 }}
              >
                {readinessData.exceptions.jobsMissingMaterialQuantity} jobs missing qty
              </MDBox>
              <MDBox
                component="span"
                sx={{ px: 1, py: 0.25, borderRadius: 999, background: "#fff3cd", fontSize: 12 }}
              >
                {readinessData.exceptions.materialLinesWithoutTrucks} lines without trucks
              </MDBox>
            </MDBox>
          </Card>
        </Grid>

        <Grid item xs={12} md={8}>
          <Card>
            <MDBox p={2}>
              <MDTypography variant="h6">Job Readiness ({dayTitle})</MDTypography>
            </MDBox>
            <MDBox sx={{ overflowX: "auto" }}>
              <DispatchReadinessTable
                jobs={readinessData.jobs}
                emptyText="No scheduled jobs found for this day."
                onOpenChecklist={setSelectedChecklistPhaseId}
                onOpenWorkOrder={setSelectedWorkOrderPhaseId}
                thStyle={thStyle}
                tdStyle={tdStyle}
              />
            </MDBox>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <MDBox p={2}>
              <MDTypography variant="h6">Daily Material Totals</MDTypography>
            </MDBox>
            <MDBox p={2} sx={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={thStyle}>Plant</th>
                    <th style={thStyle}>Material</th>
                    <th style={{ ...thStyle, textAlign: "right" }}>Qty</th>
                    <th style={{ ...thStyle, textAlign: "right" }}>Trucks</th>
                  </tr>
                </thead>
                <tbody>
                  {readinessData.dailyTotals.length === 0 ? (
                    <tr>
                      <td style={tdStyle} colSpan={4}>
                        No daily totals found.
                      </td>
                    </tr>
                  ) : (
                    readinessData.dailyTotals.flatMap((day) =>
                      day.materialRows.map((line, index) => (
                        <tr
                          key={`${day.scheduledDate}-${line.sourceName}-${line.materialName}-${index}`}
                        >
                          <td style={tdStyle}>{line.sourceName}</td>
                          <td style={tdStyle}>{line.materialName}</td>
                          <td style={{ ...tdStyle, textAlign: "right" }}>
                            {formatQty(line.quantity)} {line.unitAbbr}
                          </td>
                          <td style={{ ...tdStyle, textAlign: "right" }}>{line.trucks}</td>
                        </tr>
                      ))
                    )
                  )}
                </tbody>
              </table>
            </MDBox>
          </Card>
        </Grid>
      </Grid>
      <DispatchChecklistModal
        open={Boolean(selectedChecklistPhaseId)}
        jobPhaseId={selectedChecklistPhaseId}
        onClose={handleCloseChecklistModal}
      />
      <WorkOrderModal
        open={Boolean(selectedWorkOrderPhaseId)}
        jobPhaseId={selectedWorkOrderPhaseId}
        onClose={handleCloseWorkOrderModal}
        hideTrigger
      />
    </MDBox>
  );
}

const thStyle = {
  fontSize: 12,
  color: "#6b778c",
  fontWeight: 700,
  textAlign: "left" as const,
  padding: "8px 10px",
  borderBottom: "1px solid #dde3ec",
  background: "#f9fbfd",
};

const tdStyle = {
  fontSize: 12,
  color: "#253347",
  textAlign: "left" as const,
  padding: "8px 10px",
  borderBottom: "1px solid #edf2f7",
  verticalAlign: "top" as const,
};
